import {
  Events,
  TrackingProperties,
  useCommonTracking,
  useSectionName,
} from 'common-tracking';
import {
  ComponentClass,
  FC,
  forwardRef,
  RefObject,
  useCallback,
  useRef,
} from 'react';

type ForwardedRefType<T> = Parameters<Parameters<typeof forwardRef<T>>[0]>[1];

export function useForwardRef<HtmlType>(
  forwardedRef: ForwardedRefType<HtmlType>,
): RefObject<HtmlType> {
  const ref = useRef<HtmlType>(null);
  if (!forwardedRef) {
    return ref;
  }
  if (typeof forwardedRef === 'function') {
    forwardedRef(ref.current);
    return ref;
  } else {
    return forwardedRef;
  }
}

type InnerComponentProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (...args: any[]) => void;
  disableTracking?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDragStart?: (...args: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};
// eslint-disable-next-line
type ComponentType<P = {}> = FC<P> | ComponentClass<P>;
interface extendedHTMLElement extends HTMLElement {
  alt?: string;
}

export type Builder = (input: {
  ref: HTMLElement;
  properties: {
    sectionName?: string | undefined;
    trackingName?: string;
    typeofCTA: string | undefined;
    CTALabel: string;
  };
}) => Events;
export const withGenericClick =
  (builder: Builder) =>
  <P extends InnerComponentProps>(InnerComponent: ComponentType<P>) => {
    const EnhancedComponent = forwardRef<
      HTMLElement,
      InnerComponentProps & {
        trackingName?: string;
        trackingProperties?: TrackingProperties;
        disableTracking?: boolean;
        disableGa4Tracking?: boolean;
      }
    >((props, forwardedRef) => {
      const sectionName = useSectionName();
      const {
        onClick,
        onDragStart,
        trackingName,
        trackingProperties,
        disableTracking,
        disableGa4Tracking,
        ...rest
      } = props;
      const actualRef = useRef<extendedHTMLElement>(null);
      const mergedRef = useCallback(
        (instance: extendedHTMLElement | null) => {
          // Update inner ref
          (
            actualRef as React.MutableRefObject<extendedHTMLElement | null>
          ).current = instance;

          // Update forwarded ref
          if (typeof forwardedRef === 'function') {
            forwardedRef(instance);
          } else if (forwardedRef) {
            (
              forwardedRef as React.MutableRefObject<extendedHTMLElement | null>
            ).current = instance;
          }
        },
        [forwardedRef],
      );

      const { trackCommonEvent } = useCommonTracking();
      const forwardProps = rest as unknown as P;
      const handleClick = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (...args: any[]) => {
          const current = actualRef.current;
          if (!current) {
            if (onClick) {
              return onClick(...args);
            }
            return;
          }
          let label;
          if (current.innerText) {
            label = current.innerText;
          } else if (current.alt) {
            label = current.alt;
          } else {
            label = '';
          }
          const event: Events = {
            ...builder({
              ref: current,
              properties: {
                sectionName: sectionName,
                trackingName,
                typeofCTA: actualRef.current.nodeName,
                CTALabel: label,
              },
            }),
          };
          if (!disableTracking) {
            trackCommonEvent(event, {
              ...trackingProperties,
              disableGa4Tracking,
            });
          }
          if (onClick) {
            return onClick(...args);
          }
        },
        [
          onClick,
          trackingName,
          trackingProperties,
          sectionName,
          trackCommonEvent,
          actualRef,
          disableTracking,
          disableGa4Tracking,
        ],
      );
      const handleDrag = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (...args: any[]) => {
          const current = actualRef.current;
          if (!current) {
            if (onDragStart) {
              return onDragStart(...args);
            }
            return;
          }
          const label = current.innerText ?? '';
          const event: Events = {
            ...builder({
              ref: current,
              properties: {
                sectionName: sectionName,
                trackingName,
                typeofCTA: actualRef.current.nodeName,
                CTALabel: label,
              },
            }),
          };
          if (!disableTracking) {
            trackCommonEvent(event, {
              ...trackingProperties,
              disableGa4Tracking,
            });
          }
          if (onDragStart) {
            return onDragStart(...args);
          }
        },
        [
          onDragStart,
          trackingName,
          trackingProperties,
          sectionName,
          trackCommonEvent,
          actualRef,
          disableTracking,
          disableGa4Tracking,
        ],
      );
      return (
        <InnerComponent
          ref={mergedRef}
          data-tracking-name={props.trackingName}
          {...forwardProps}
          onClick={handleClick}
          onDragStart={handleDrag}
        />
      );
    });

    return EnhancedComponent;
  };
